import React from 'react';
import { tv } from 'tailwind-variants';
import { twMerge } from '@/utils';
import Icon from '@/components/ui/Icon';

interface DropdownButtonProps {
  checkmarkPlacement?: 'left' | 'right' | 'none';
  children: ReactChildren;
  className?: string;
  disabled?: boolean;
  href?: string;
  onClick?: (event?: React.MouseEvent) => void;
  onKeyDown?: (event: React.KeyboardEvent) => void;
  onSelect?: () => void;
  selected?: boolean;
  title?: string;
  type?: 'button' | 'link';
}

const buttonVariants = tv({
  base: [
    'tw-cursor-pointer reading-level-choice dropdown-item tw-py-2 -tw-outline-offset-1',
  ],
  variants: {
    checkmarkPlacement: {
      left: 'tw-flex tw-items-center',
      none: '',
      right: 'tw-flex tw-items-center',
    },
    disabled: {
      true: '!tw-cursor-not-allowed !tw-pointer-events-auto tw-text-grey',
    },
    type: {
      button: 'tw-w-full',
      link: 'tw-w-auto',
    },
  },
});

const DropdownButton = React.forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  DropdownButtonProps
>(({
  checkmarkPlacement = 'none',
  children,
  className = '',
  disabled = false,
  href,
  onClick = () => {},
  onKeyDown,
  onSelect,
  selected = false,
  title,
  type = 'button',
}, ref) => {
  const ButtonComponent = type === 'link' ? 'a' : 'button';

  const handleClick = (e: React.MouseEvent) => {
    if (!disabled) {
      onClick(e);
      onSelect?.();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (onKeyDown) onKeyDown(e);

    if (([' ', 'Enter'].includes(e.key)) && onClick && !disabled) {
      e.preventDefault();
      onClick();
    }

    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.preventDefault();
      const currentElement = e.currentTarget as HTMLElement;
      const dropdownContent = currentElement.parentElement;

      if (dropdownContent) {
        const focusableItems = Array.from(
          dropdownContent.querySelectorAll('button:not([disabled])'),
        ) as HTMLElement[];

        const currentIndex = focusableItems.indexOf(currentElement);
        let nextIndex: number;

        if (['ArrowDown', 'ArrowRight'].includes(e.key)) {
          nextIndex = (currentIndex + 1) % focusableItems.length;
        } else {
          nextIndex = (currentIndex - 1 < 0) ? focusableItems.length - 1 : currentIndex - 1;
        }

        focusableItems[nextIndex]?.focus();
      }
    }
  };

  return (
    <ButtonComponent
      ref={ref as any}
      className={twMerge(
        buttonVariants({ checkmarkPlacement, disabled, type }),
        className,
      )}
      disabled={disabled}
      href={href}
      title={title}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {checkmarkPlacement === 'left' && (
        <span className="tw-inline-block tw-w-5">
          {selected && (
            <i className="fa fa-check tw-text-green" />
          )}
        </span>
      )}
      <span className="tw-w-full">{children}</span>
      {checkmarkPlacement === 'right' && (
        <div className="tw-flex tw-items-center tw-justify-center tw-ml-auto tw-w-5">
          {selected && (
            <Icon fill="#2A8806" icon="Checkmark" size={24} />
          )}
        </div>
      )}
    </ButtonComponent>
  );
});

DropdownButton.displayName = 'Dropdown.Button';

export default DropdownButton;
